/**
 * The room's fields: name, length, width, ceiling height, ceiling shape, and
 * the kind of space it is.
 *
 * Two forms:
 *   create — a blank sheet: a centred card to size the first room by numbers
 *            (no drawing), with one Create button. Nothing is committed until
 *            it is pressed: the fields are the card's own state.
 *   panel  — a room is selected: the same fields, live (each edit commits),
 *            plus "Design your Space" (to 3D) and Delete. Rendered inside the
 *            planner's panel at the top right.
 */

import React, { useMemo, useState } from 'react';
import { Box, Check, Trash2 } from 'lucide-react';
import { DimensionInput } from '../../ui/DimensionInput';
import { CeilingEditor } from './CeilingEditor';
import { SpacePicker } from '../chrome/SpaceMenu';
import { CeilingProfile } from '../../../calculations/ceiling/profile';
import {
  EMPTY_PLAN, FloorPlan, PlanResult, PlanRoom, ROOM_LIMITS, addRoom, copyCeiling, rectOfRoom, renameRoom, setCeilingProfile, setRoomRect, setRoomsHeight,
} from '../../../calculations/plan';
import { DESIGNER_STRINGS as S } from '../strings';

const P = S.planner;
const HALF_INCH_FT = 1 / 24;

/** What the create form hands back. */
export interface CreateRoomForm {
  name: string;
  length: number;
  width: number;
  height: number;
  ceilingProfile?: CeilingProfile;
  spaceType: string;
}

const labelStyle: React.CSSProperties = { display: 'block', fontWeight: 700, marginBottom: 8 };
const inputStyle: React.CSSProperties = {
  display: 'block', width: '100%', marginTop: 4, padding: '5px 8px', borderRadius: 6,
  border: '1px solid rgba(255,255,255,0.25)', background: 'rgba(255,255,255,0.08)', color: '#fff', fontSize: 13,
};

const Field: React.FC<{ label: string; children: React.ReactNode; title?: string }> = ({ label, children, title }) => (
  <label style={labelStyle} title={title}>
    {label}
    <span style={{ display: 'block', marginTop: 4 }}>{children}</span>
  </label>
);

// ---------------------------------------------------------------------------
// Create
// ---------------------------------------------------------------------------

export interface CreateRoomDialogProps {
  variant: 'create';
  defaultName: string;
  defaultHeight: number;
  spaceType: string;
  onCreate: (form: CreateRoomForm) => void;
}

const DEFAULT_LENGTH = 16;
const DEFAULT_WIDTH = 12;

const CreateRoomDialog: React.FC<CreateRoomDialogProps> = ({ defaultName, defaultHeight, spaceType: initialSpace, onCreate }) => {
  const [name, setName] = useState(defaultName);
  const [length, setLength] = useState(DEFAULT_LENGTH);
  const [width, setWidth] = useState(DEFAULT_WIDTH);
  const [height, setHeight] = useState(defaultHeight);
  const [profile, setProfile] = useState<CeilingProfile | undefined>(undefined);
  const [spaceType, setSpaceType] = useState(initialSpace);

  // The room as it would be, for the ceiling editor's cross-section.
  const draft = useMemo<PlanRoom | null>(() => {
    const added = addRoom(EMPTY_PLAN, { x: 0, z: 0, length, width }, height, name.trim() || defaultName);
    if (!added.ok) return null;
    const room = added.plan.rooms[0];
    if (!profile || profile.kind === 'flat') return room;
    const shaped = setCeilingProfile(added.plan, [room.id], profile);
    return shaped.ok ? shaped.plan.rooms[0] : room;
  }, [length, width, height, name, defaultName, profile]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    onCreate({ name: name.trim() || defaultName, length, width, height, ceilingProfile: profile, spaceType });
  };

  return (
    <form
      className="designer-toolbar floor-planner-create"
      onSubmit={submit}
      onPointerDown={(e) => e.stopPropagation()}
      style={{
        position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)',
        width: 320, maxWidth: 'calc(100% - 16px)', maxHeight: 'calc(100% - 16px)', overflowY: 'auto', boxSizing: 'border-box',
        padding: '14px 16px 16px', borderRadius: 12, background: 'rgba(26,26,30,0.94)', color: '#fff',
        border: '1px solid rgba(255,255,255,0.12)', boxShadow: '0 12px 40px rgba(0,0,0,0.5)', fontSize: 12, cursor: 'default',
      }}
    >
      <p style={{ margin: '0 0 2px', fontWeight: 700, fontSize: 15 }}>{P.createTitle}</p>
      <p style={{ margin: '0 0 12px', color: '#c9cbd3', lineHeight: 1.45 }}>{P.createIntro}</p>
      <label style={labelStyle}>
        {P.name}
        <input type="text" value={name} maxLength={40} aria-label={P.name} autoFocus onChange={(e) => setName(e.target.value)} style={inputStyle} />
      </label>
      <Field label={P.length}>
        <DimensionInput value={length} mode="ftin" min={ROOM_LIMITS.side.min} max={ROOM_LIMITS.side.max} ariaLabel={P.length} onChange={setLength} />
      </Field>
      <Field label={P.width}>
        <DimensionInput value={width} mode="ftin" min={ROOM_LIMITS.side.min} max={ROOM_LIMITS.side.max} ariaLabel={P.width} onChange={setWidth} />
      </Field>
      <Field label={P.height}>
        <DimensionInput value={height} mode="ftin" min={ROOM_LIMITS.height.min} max={ROOM_LIMITS.height.max} ariaLabel={P.height} onChange={setHeight} />
      </Field>
      {draft && <CeilingEditor room={draft} count={1} maxHeight={ROOM_LIMITS.height.max} onChange={setProfile} />}
      <div style={{ marginTop: 12 }}>
        <p style={{ margin: '0 0 6px', fontWeight: 700 }}>{P.space}</p>
        <SpacePicker value={spaceType} onChange={setSpaceType} />
      </div>
      <button type="submit" className="dh-btn is-accent" style={{ width: '100%', height: 36, marginTop: 14, fontSize: 13 }}>
        <Check size={15} /> {P.create}
      </button>
    </form>
  );
};

// ---------------------------------------------------------------------------
// Panel
// ---------------------------------------------------------------------------

export interface PanelRoomDialogProps {
  variant: 'panel';
  plan: FloorPlan;
  selectedRooms: PlanRoom[];
  /** The one selected room, when exactly one is. */
  sole: PlanRoom | null;
  /** Commit a result (the planner's `apply`): refuses and notices are its business. */
  apply: (result: PlanResult, coalesceKey?: string) => boolean;
  spaceType: string;
  onSpaceType?: (id: string) => void;
  /** Open the sole room in 3D. */
  onDesign: () => void;
  onDelete: () => void;
}

const PanelRoomDialog: React.FC<PanelRoomDialogProps> = ({ plan, selectedRooms, sole, apply, spaceType, onSpaceType, onDesign, onDelete }) => {
  const selectedIds = selectedRooms.map((r) => r.id);
  if (selectedRooms.length === 0) return null;
  return (
    <>
      {sole && (
        <>
          <label style={labelStyle}>
            {P.name}
            <input
              type="text" value={sole.name} maxLength={40} aria-label={P.name}
              onChange={(e) => apply(renameRoom(plan, sole.id, e.target.value), `${sole.id}:name`)}
              style={inputStyle}
            />
          </label>
          {([
            { key: 'length', label: P.length, value: sole.length, set: (v: number) => apply(setRoomRect(plan, sole.id, { ...rectOfRoom(sole), length: v }), `${sole.id}:length`) },
            { key: 'width', label: P.width, value: sole.width, set: (v: number) => apply(setRoomRect(plan, sole.id, { ...rectOfRoom(sole), width: v }), `${sole.id}:width`) },
          ]).map((f) => (
            <Field key={f.key} label={f.label}>
              <DimensionInput
                value={f.value} mode="ftin" min={ROOM_LIMITS.side.min} max={ROOM_LIMITS.side.max} ariaLabel={f.label}
                onChange={(v) => { if (Math.abs(v - f.value) >= HALF_INCH_FT) f.set(v); }}
              />
            </Field>
          ))}
        </>
      )}
      {selectedRooms.length > 1 && <p style={{ margin: '0 0 8px', fontWeight: 700, fontSize: 13 }}>{P.selected(selectedRooms.length)}</p>}
      <Field label={P.height} title={selectedRooms.length > 1 ? P.heightAll : undefined}>
        <DimensionInput
          value={selectedRooms[0].height} mode="ftin" min={ROOM_LIMITS.height.min} max={ROOM_LIMITS.height.max} ariaLabel={P.height}
          onChange={(v) => { if (Math.abs(v - selectedRooms[0].height) >= HALF_INCH_FT) apply(setRoomsHeight(plan, selectedIds, v), `${selectedIds.join('+')}:height`); }}
        />
      </Field>
      {selectedRooms.length > 1 && <p style={{ margin: '0 0 8px', color: '#a9abb3' }}>{P.heightAll}</p>}
      <CeilingEditor
        room={selectedRooms[0]} count={selectedRooms.length} maxHeight={ROOM_LIMITS.height.max}
        onChange={(profile) => apply(setCeilingProfile(plan, selectedIds, profile), `${selectedIds.join('+')}:ceiling`)}
        others={sole ? plan.rooms.filter((r) => r.id !== sole.id).map((r) => ({ id: r.id, name: r.name })) : undefined}
        onCopyTo={sole ? (ids) => apply(copyCeiling(plan, sole.id, ids)) : undefined}
      />
      {onSpaceType && (
        <div style={{ marginTop: 12 }}>
          <p style={{ margin: '0 0 6px', fontWeight: 700 }}>{P.space}</p>
          <SpacePicker value={spaceType} onChange={onSpaceType} />
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        {sole && (
          <button type="button" className="dh-btn is-accent" style={{ flex: '1 1 auto', height: 32 }} onClick={onDesign}>
            <Box size={14} /> {P.designSpace}
          </button>
        )}
        <button
          type="button" className="dh-btn" style={{ flex: sole ? '0 0 auto' : '1 1 auto', height: 32 }}
          onClick={onDelete} title={sole ? P.delete : P.deleteMany(selectedRooms.length)} aria-label={sole ? P.delete : P.deleteMany(selectedRooms.length)}
        >
          <Trash2 size={14} /> {sole ? '' : P.deleteMany(selectedRooms.length)}
        </button>
      </div>
    </>
  );
};

export type RoomDialogProps = CreateRoomDialogProps | PanelRoomDialogProps;

export const RoomDialog: React.FC<RoomDialogProps> = (props) => (
  props.variant === 'create' ? <CreateRoomDialog {...props} /> : <PanelRoomDialog {...props} />
);

export default RoomDialog;
